/**
 * Sidebar-Gruppen (Joerg 2026-09-17) — Single Source of Truth fuer die fuenf
 * Arbeitsbereich-Gruppen der Desktop-Sidebar.
 *
 * Wird von PortalSidebar (Rendering) und AdminSidebarLabelsPage (Gruppen-
 * Auswahl je Eintrag) importiert. Importiert selbst NICHTS aus PortalSidebar
 * (kein Zirkel). Die Slug-Whitelist im Backend (routes/admin.ts,
 * SIDEBAR_GROUP_SLUGS) muss zu SIDEBAR_GROUPS passen.
 *
 * Gruppen-Slug `wissen-dokumente` statt `wissen`, weil `wissen` ein Modul-Slug
 * ist (Kollision in Overrides/Blacklists). `mehr` ist keine echte Gruppe,
 * sondern das persoenliche Mehr-Menue (Pseudo-Gruppe, immer zuletzt).
 */
import type { ComponentType } from 'react';
import { Briefcase, Cog, FolderOpen, HardHat, MoreHorizontal, UserRound } from 'lucide-react';

export type SidebarGroupSlug = 'mein-bereich' | 'baustelle' | 'buero' | 'wissen-dokumente' | 'verwaltung';
export type SidebarGroupKey = SidebarGroupSlug | 'mehr';

export type SidebarGroupIcon = ComponentType<{ size?: number; className?: string }>;

export interface SidebarGroupDef {
  slug: SidebarGroupSlug;
  label: string;
  icon: SidebarGroupIcon;
  order: number;
}

export const SIDEBAR_GROUPS: readonly SidebarGroupDef[] = [
  { slug: 'mein-bereich',     label: 'Mein Bereich',        icon: UserRound,  order: 10 },
  { slug: 'baustelle',        label: 'Baustelle',           icon: HardHat,    order: 20 },
  { slug: 'buero',            label: 'Büro',                icon: Briefcase,  order: 30 },
  { slug: 'wissen-dokumente', label: 'Wissen & Dokumente',  icon: FolderOpen, order: 40 },
  { slug: 'verwaltung',       label: 'Verwaltung',          icon: Cog,        order: 50 },
];

/** Pseudo-Gruppe fuer das persoenliche Mehr-Menue (SP1 2026-07-21), immer zuletzt. */
export const MEHR_GROUP: { slug: 'mehr'; label: string; icon: SidebarGroupIcon } = {
  slug: 'mehr', label: 'Mehr', icon: MoreHorizontal,
};

export const SIDEBAR_GROUP_SLUGS: readonly SidebarGroupSlug[] = SIDEBAR_GROUPS.map((g) => g.slug);

export function isSidebarGroupSlug(v: unknown): v is SidebarGroupSlug {
  return typeof v === 'string' && (SIDEBAR_GROUP_SLUGS as readonly string[]).includes(v);
}

export function groupDef(slug: SidebarGroupSlug): SidebarGroupDef {
  return SIDEBAR_GROUPS.find((g) => g.slug === slug) ?? SIDEBAR_GROUPS[2]!;
}

/** Tenant-Override je Slug (portal_sidebar_label_overrides). */
export interface SidebarOverride {
  alias: string | null;
  hidden: boolean;
  /** Gruppen-Slug oder null = Code-Default (seit 2026-09-17). */
  section: string | null;
}

/**
 * Slug-Fallback fuer dynamisch registrierte Module, deren self-registry.ts noch
 * `nav_section: 'Anwendungen'` meldet (Stand 17.09.: alle). Sobald die Container
 * die neuen Gruppen-Slugs melden, greift das Mapping unten direkt und diese
 * Tabelle wird ueberfluessig.
 */
const DYNAMIC_GROUP_DEFAULTS: Record<string, SidebarGroupSlug> = {
  faktura: 'buero',
  fleet: 'verwaltung',
  wissen: 'wissen-dokumente',
  kalkulation: 'buero',
  aufmass: 'baustelle',
  recovery: 'verwaltung',
};

/**
 * `portal_module_registry.nav_section` (freier String vom Container) auf eine
 * Gruppe abbilden. Ersetzt das alte Zwei-Kategorien-Mapping („personen*" →
 * Personen & Termine, alles andere → Anwendungen).
 */
export function mapNavSectionToGroup(section: string | null | undefined, moduleSlug: string): SidebarGroupSlug {
  const s = (section ?? '').trim().toLowerCase();
  if (isSidebarGroupSlug(s)) return s;
  if (s === 'mein bereich' || s === 'persoenlich' || s === 'persönlich') return 'mein-bereich';
  if (s === 'büro' || s === 'buero' || s === 'office') return 'buero';
  if (s === 'wissen' || s === 'dokumente' || s === 'wissen & dokumente') return 'wissen-dokumente';
  if (s === 'admin' || s === 'verwaltung') return 'verwaltung';
  if (s.startsWith('personen')) return 'buero'; // Legacy „Personen & Termine"
  return DYNAMIC_GROUP_DEFAULTS[moduleSlug] ?? 'buero';
}

/** Effektive Gruppe: Tenant-Override (wenn gueltig) > Code-Default. */
export function resolveGroup(defaultGroup: SidebarGroupSlug, override?: string | null): SidebarGroupSlug {
  return isSidebarGroupSlug(override) ? override : defaultGroup;
}
